import { getStoriesByIds, Story } from '../Entities/Stories/stories.reducer';
import { isType } from 'typescript-fsa';
import {
  hideClusterEditLoading,
  resetClusterEdit,
  showClusterEditLoading,
  updateClusterEdit,
  updateClusterEditName,
  updateClusterEditStories,
  updateClusterEditText,
  submitClusterEdit,
  toggleIsFeaturedCluster,
  updateActivePeriod,
  updateGender,
  updateType,
  updateClusterEditImage,
} from './cluster-edit.actions';
import { AppState } from '../reducers';
import { getQueryParameter } from '../../utils/url';
import { Action } from 'redux';
import { ImmutableObject } from 'seamless-immutable';
import * as Immutable from 'seamless-immutable';
import { LOCATION_CHANGE } from 'react-router-redux';
import { stopAllLoaders } from '../Config/config.actions';
import {
  fillTranslatableWithFallback,
  fillEquallySpokenTranslatables,
  countries,
} from '../../utils/country';
import { sortBy } from 'ramda';
import { ClusterImage } from '../../modules/ClusterEdit';

export interface ClusterEdit {
  id?: number;
  name: string;
  headline: {
    [key: string]: string;
  };
  subline: {
    [key: string]: string;
  };
  text: {
    [key: string]: string;
  };
  stories: number[] | Story[];
  country: string | null;
  isFeatured: {
    [countryShortCode: string]: boolean;
  };
  activePeriod: {
    [countryShortCode: string]: {
      activeFrom: string | null;
      activeTo: string | null;
    };
  };
  gender: 'female' | 'male';
  loading: boolean;
  type: string;
  images?: {
    data: ClusterImage[]
  };
}

export type ClusterEditState = ClusterEdit & ImmutableObject<ClusterEdit>;

const initialState = Immutable.from<ClusterEdit>({
  id: void 0,
  name: '',
  headline: {},
  subline: {},
  text: {},
  stories: [],
  country: 'de',
  isFeatured: {},
  activePeriod: {},
  gender: 'female',
  loading: false,
  type: 'default',
  images: { data: [] },
});

export default function reduce(
  state: ClusterEditState = initialState,
  action: Action
) {
  if (action.type === LOCATION_CHANGE) {
    const { search, pathname } = (action as any).payload;
    const regExp = new RegExp(/^\/clusters\/\d+\/edit/);
    if (!regExp.test(pathname)) {
      return state;
    }

    return state.merge({
      country: getQueryParameter('country', search, 'de'),
    });
  }

  if (isType(action, updateClusterEdit)) {
    const {
      payload: { cluster },
    } = action;

    if (!cluster) {
      return state;
    }

    return state.merge(cluster);
  }

  if (isType(action, updateClusterEditText)) {
    const {
      payload: { type, language, text },
    } = action;

    return state.merge(
      {
        [type]: fillEquallySpokenTranslatables(language, text),
      },
      { deep: true }
    );
  }

  if (isType(action, toggleIsFeaturedCluster)) {
    const {
      payload: { countryShortCode },
    } = action;

    return state.merge({
      isFeatured: {
        ...state.isFeatured,
        [countryShortCode]: !state.isFeatured[countryShortCode],
      },
    });
  }

  if (isType(action, updateActivePeriod)) {
    const {
      payload: { period, countryShortCode },
    } = action;

    return state.merge({
      activePeriod: { ...state.activePeriod, [countryShortCode]: period },
    });
  }

  if (isType(action, updateGender)) {
    const {
      payload: { gender },
    } = action;

    return state.merge({ gender });
  }

  if (isType(action, updateType)) {
    const {
      payload: { type },
    } = action;

    return state.merge({ type });
  }

  if (isType(action, submitClusterEdit)) {
    const { headline, subline, text } = state;

    const newHeadline = headline.de
      ? fillTranslatableWithFallback('de', headline.de, headline)
      : headline;

    const newSubline = subline.de
      ? fillTranslatableWithFallback('de', subline.de, subline)
      : subline;

    const newText = text.de
      ? fillTranslatableWithFallback('de', text.de, text)
      : subline;

    return state.merge(
      {
        headline: newHeadline,
        subline: newSubline,
        text: newText,
      },
      { deep: true }
    );
  }

  if (isType(action, updateClusterEditName)) {
    const {
      payload: { name },
    } = action;
    return state.merge({
      name,
    });
  }

  if (isType(action, updateClusterEditImage)) {
    const {
      payload: { images },
    } = action;
    return state.merge({
      images,
    });
  }

  if (isType(action, updateClusterEditStories)) {
    const {
      payload: { stories },
    } = action;
    return state.merge({
      stories: stories as any[],
    });
  }

  if (isType(action, resetClusterEdit)) {
    return initialState;
  }

  if (isType(action, showClusterEditLoading)) {
    return state.merge({
      loading: true,
    });
  }

  if (isType(action, hideClusterEditLoading)) {
    return state.merge({
      loading: false,
    });
  }

  if (isType(action, stopAllLoaders)) {
    return state.merge({ loading: false });
  }

  return state;
}

export function getCountry(state: AppState) {
  const {
    clusterEdit: { country },
  } = state;

  return country;
}

export function getClusterEditById(state: AppState, clusterId: number) {
  const { clusterEdit } = state;
  if (clusterEdit.id === clusterId) {
    const stories = getStoriesByIds(state, clusterEdit.stories as number[]);
    return {
      ...clusterEdit,
      stories,
    } as ClusterEditState;
  }
  return false;
}

export function getCurrentClusterEditStoryIds(state: AppState) {
  const { clusterEdit } = state;
  return clusterEdit && clusterEdit.stories;
}

export function getCurrentEditClusterState(state: AppState) {
  const { clusterEdit } = state;
  const stories = getStoriesByIds(state, clusterEdit.stories as number[]);

  return {
    ...clusterEdit,
    stories: sortBy(
      story => (story.clusterStory && story.clusterStory.position) || 0,
      stories
    ),
  };
}

export function getClusterEdit(state: AppState) {
  const { clusterEdit } = state;

  return clusterEdit;
}

export function getIsFeaturedCluster(state: AppState) {
  const { isFeatured } = state.clusterEdit;

  return isFeatured;
}

export function getIsFeaturedClusterAppIds(state: AppState) {
  return Object.keys(state.clusterEdit.isFeatured).reduce((acc, cur) => {
    const country = countries.find(c => c.shortcode === cur);

    if (!country || !state.clusterEdit.isFeatured[cur]) {
      return acc;
    }

    return [...acc, country.id];
  }, []);
}



// WEBPACK FOOTER //
// ./src/store/ClusterEdit/cluster-edit.reducer.ts